import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { ChangePasswordForm } from '@ecom/ui-kit';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useChangePasswordMutation } from '../api';
import { AuthLayout } from '../components/AuthLayout';

import { useSession } from '@/lib/session';

/**
 * The screen a provisional password leads to - and the way to change one's password at any time.
 *
 * Reached (1) straight after a sign-in whose answer says `mustChangePassword`, and (2) from anywhere when the API answers
 * `PASSWORD_CHANGE_REQUIRED`. The person types the provisional password they were given and a password of their own; on success the
 * API has ended EVERY earlier session and returned a fresh one, which replaces this one. Nothing is kept: the passwords live in the form's own state and the mutation is collected at once.
 */
export function ChangePasswordPage(): ReactElement {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mustChangePassword, setSession, clearSession } = useSession();
  const mutation = useChangePasswordMutation();
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(values: { currentPassword: string; newPassword: string }): Promise<void> {
    setError(null);
    try {
      const fresh = await mutation.mutateAsync(values);
      mutation.reset();
      // Everything cached belongs to a session that no longer exists.
      queryClient.clear();
      setSession({
        accessToken: fresh.accessToken,
        refreshToken: fresh.refreshToken,
        mustChangePassword: fresh.mustChangePassword,
      });
      void navigate('/', { replace: true });
    } catch (failure) {
      mutation.reset();
      setError(t(resolveErrorMessageKey(failure)));
      throw failure;
    }
  }

  return (
    <AuthLayout
      title={t('auth.changePassword.title')}
      subtitle={
        mustChangePassword ? t('auth.changePassword.subtitle') : t('common.changePassword.rules')
      }
      footer={
        <button
          type="button"
          className="font-medium text-accent hover:underline"
          onClick={() => {
            clearSession();
            void navigate('/login', { replace: true });
          }}
        >
          {t('auth.changePassword.signOut')}
        </button>
      }
    >
      <ChangePasswordForm
        onSubmit={onSubmit}
        error={error}
        busy={mutation.isPending}
        {...(mustChangePassword ? { currentLabel: t('auth.changePassword.currentLabel') } : {})}
      />
    </AuthLayout>
  );
}
